import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, where, orderBy, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../contexts/authContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getModerationDisplay } from '../utils/aiModeration';

const AdminModeration = () => {
  const { user, loading, isAdmin, ready } = useAuth();
  const navigate = useNavigate();
  const [pendingGames, setPendingGames] = useState([]);
  const [pendingSlides, setPendingSlides] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [activeTab, setActiveTab] = useState('games');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    // Wait for auth to be ready
    if (!ready) return;
    
    // Only redirect if we're sure the user is not an admin (use comprehensive check)
    if (!loading && user && !isAdmin) {
      console.log('❌ AdminModeration: User is not admin, redirecting');
      navigate('/not-authorized');
      return;
    }
    
    // Only fetch content if user is admin
    if (user && isAdmin) {
      fetchPendingContent();
    }
  }, [user, isAdmin, loading, ready, navigate]);

  const fetchPendingContent = async () => {
    try {
      setLoadingContent(true);
      
      // Fetch pending games
      let gamesData = [];
      try {
        const gamesQuery = query(
          collection(db, 'games', 'content', 'triviaGames'),
          where('marketplaceData.approvalStatus', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        const gamesSnapshot = await getDocs(gamesQuery);
        gamesData = gamesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'game'
        }));
      } catch (error) {
        console.log('Index is still building for pending games query. This is normal for new indexes.');
        // Fallback: fetch all games and filter client-side
        try {
          const allGamesQuery = query(
            collection(db, 'games', 'content', 'triviaGames'),
            orderBy('createdAt', 'desc')
          );
          const allGamesSnapshot = await getDocs(allGamesQuery);
          gamesData = allGamesSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              type: 'game'
            }))
            .filter(game => game.marketplaceData?.approvalStatus === 'pending');
        } catch (fallbackError) {
          console.error('Error fetching games with fallback:', fallbackError);
        }
      }
      setPendingGames(gamesData);

      // Fetch pending slides
      let slidesData = [];
      try {
        const slidesQuery = query(
          collection(db, 'slides'),
          where('approved', '==', false),
          orderBy('createdAt', 'desc')
        );
        const slidesSnapshot = await getDocs(slidesQuery);
        slidesData = slidesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'slide'
        }));
      } catch (error) {
        console.log('No slides found or collection does not exist yet');
      }
      setPendingSlides(slidesData);

    } catch (error) {
      console.error('Error fetching pending content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleApprove = async (item) => {
    try {
      if (item.type === 'game') {
        await updateDoc(doc(db, 'games', 'content', 'triviaGames', item.id), {
          'marketplaceData.approvalStatus': 'approved',
          'marketplaceData.approvedAt': new Date(),
          'marketplaceData.approvedBy': user.uid,
          status: 'public',
          updatedAt: new Date()
        });
      } else if (item.type === 'slide') {
        await updateDoc(doc(db, 'slides', item.id), {
          approved: true,
          approvedAt: new Date(),
          approvedBy: user.uid,
          updatedAt: new Date()
        });
      }
      
      fetchPendingContent();
      setShowDetailModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error approving content:', error);
      alert('Error approving content');
    }
  };

  const handleReject = async (item, reason) => {
    try {
      if (item.type === 'game') {
        await updateDoc(doc(db, 'games', 'content', 'triviaGames', item.id), {
          'marketplaceData.approvalStatus': 'rejected',
          'marketplaceData.rejectedAt': new Date(),
          'marketplaceData.rejectedBy': user.uid,
          'marketplaceData.rejectionReason': reason,
          status: 'rejected',
          updatedAt: new Date()
        });
      } else if (item.type === 'slide') {
        await updateDoc(doc(db, 'slides', item.id), {
          approved: false,
          rejectedAt: new Date(),
          rejectedBy: user.uid,
          rejectionReason: reason,
          updatedAt: new Date()
        });
      }
      
      fetchPendingContent();
      setShowDetailModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error rejecting content:', error);
      alert('Error rejecting content');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to permanently delete this content?')) return;

    try {
      if (item.type === 'game') {
        await deleteDoc(doc(db, 'games', 'content', 'triviaGames', item.id));
      } else if (item.type === 'slide') {
        await deleteDoc(doc(db, 'slides', item.id));
      }
      
      fetchPendingContent();
      setShowDetailModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Error deleting content');
    }
  };

  const openDetailModal = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedItem(null);
  };

  const renderModerationStatus = (item) => {
    const display = getModerationDisplay(item.moderation);
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${display.bgColor} ${display.textColor}`}>
        <span className="mr-1">{display.icon}</span>
        {display.text}
      </div>
    );
  };

  const renderContentCard = (item) => {
    const isGame = item.type === 'game';
    const title = isGame ? item.title : item.title;
    const creator = isGame ? item.creatorName : item.createdBy;
    const createdAt = item.createdAt?.toDate?.() ? 
      item.createdAt.toDate().toLocaleDateString() : 
      'Recently';

    return (
      <div key={item.id} className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">{title}</h3>
            <p className="text-sm text-gray-600">By: {creator}</p>
            <p className="text-xs text-gray-500">Created: {createdAt}</p>
          </div>
          <div className="ml-4">
            {renderModerationStatus(item)}
          </div>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-700 line-clamp-2">
            {isGame ? item.description : item.content}
          </p>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-xs text-gray-500">
            Confidence: {Math.round(item.moderation?.confidence * 100)}%
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => openDetailModal(item)}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
            >
              Review
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!selectedItem) return null;

    const isGame = selectedItem.type === 'game';
    const display = getModerationDisplay(selectedItem.moderation);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {isGame ? 'Game Review' : 'Slide Review'}
            </h2>
            <button
              onClick={closeDetailModal}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Content Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Content Details</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <p className="text-gray-900">{selectedItem.title}</p>
                </div>
                {isGame && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <p className="text-gray-900">{selectedItem.description}</p>
                  </div>
                )}
                {!isGame && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Content</label>
                    <p className="text-gray-900">{selectedItem.content}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Creator</label>
                  <p className="text-gray-900">{isGame ? selectedItem.creatorName : selectedItem.createdBy}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="text-gray-900">
                    {selectedItem.createdAt?.toDate?.() ? 
                      selectedItem.createdAt.toDate().toLocaleString() : 
                      'Recently'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* AI Moderation Results */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">AI Moderation Results</h3>
              <div className="space-y-4">
                <div className={`p-3 rounded-lg ${display.bgColor}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-lg">{display.icon}</span>
                    <span className={`font-semibold ${display.textColor}`}>{display.text}</span>
                  </div>
                  <p className={`text-sm ${display.textColor}`}>
                    Severity: {selectedItem.moderation?.severity?.toUpperCase()}
                  </p>
                  <p className={`text-sm ${display.textColor}`}>
                    Confidence: {Math.round(selectedItem.moderation?.confidence * 100)}%
                  </p>
                </div>

                {selectedItem.moderation?.issues && (
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Issues Found:</h4>
                    <div className="space-y-2">
                      {selectedItem.moderation.issues.map((issue, index) => (
                        <div key={index} className="p-2 bg-red-50 border border-red-200 rounded">
                          <p className="text-sm font-medium text-red-800">{issue.type}</p>
                          <p className="text-sm text-red-700">{issue.description}</p>
                          <p className="text-xs text-red-600 mt-1">Suggestion: {issue.suggestion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedItem.moderation?.recommendations && (
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">AI Recommendations:</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                      {selectedItem.moderation.recommendations}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => handleDelete(selectedItem)}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
            <button
              onClick={() => handleReject(selectedItem, 'Manual rejection by admin')}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => handleApprove(selectedItem)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loadingContent) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading moderation queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">🛡️ Content Moderation</h1>
          <p className="text-gray-300">Review and approve/reject content flagged by AI moderation</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Games</p>
                <p className="text-2xl font-bold text-gray-900">{pendingGames.length}</p>
              </div>
              <div className="text-3xl">🧠</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Slides</p>
                <p className="text-2xl font-bold text-gray-900">{pendingSlides.length}</p>
              </div>
              <div className="text-3xl">📢</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('games')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'games'
                    ? 'border-green-500 text-green-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                Games ({pendingGames.length})
              </button>
              <button
                onClick={() => setActiveTab('slides')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'slides'
                    ? 'border-green-500 text-green-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                Slides ({pendingSlides.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Content List */}
        <div className="space-y-4">
          {activeTab === 'games' && (
            pendingGames.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">✅</div>
                <p className="text-gray-400">No pending games to review</p>
              </div>
            ) : (
              pendingGames.map(renderContentCard)
            )
          )}
          
          {activeTab === 'slides' && (
            pendingSlides.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">✅</div>
                <p className="text-gray-400">No pending slides to review</p>
              </div>
            ) : (
              pendingSlides.map(renderContentCard)
            )
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && renderDetailModal()}
      </div>
    </div>
  );
};

export default AdminModeration; 